// src/components/Catalog.js

import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import axios from "../../utils/axiosConfig";
import * as pdfjsLib from "pdfjs-dist";
import { FaDownload } from "react-icons/fa";
import "./Catalog.css";

// Set the workerSrc property for pdfjs (use local worker for faster loads)
pdfjsLib.GlobalWorkerOptions.workerSrc = `/pdf.worker.min.js`;

const Catalog = () => {
  const { t, i18n } = useTranslation();
  const [catalogs, setCatalogs] = useState([]);
  const [error, setError] = useState("");

  // Debug: Log current language
  console.log('Current language:', i18n.language);
  console.log('Translation function available:', !!t);

  // Function to translate catalog names (robust normalization + substring match)
  const translateCatalogName = (name) => {
    if (!name) return '';

    const normalize = (s) =>
      String(s)
        .toLowerCase()
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '') // strip diacritics
        .replace(/[^a-z0-9\s]/g, ' ') // remove punctuation
        .replace(/katalogu|katalogu|katalog|kataloğu|katalofu/g, '') // common suffixes
        .replace(/\s+/g, ' ')
        .trim();

    const turkishToKeyMap = {
      'mutfak': 'kitchen',
      'bina bakimi': 'building_care',
      'bina bakim ve temizlik ürünleri': 'building_care',
      'bina bakim ve temizlik': 'building_care',
      'kisisel hijyen': 'personal_hygiene',
      'camasir': 'laundry',
      'gida hijyeni': 'food_hygiene',
      'havuz kimyasallari': 'pool_chemicals',
      'ozel urunler': 'special_products',
      'endustriyel kimya': 'industrial_chemistry',
      'equinox': 'equinox',
      'endaxi': 'endaxi',
      'lora': 'lora',
      'chemiccount': 'chemiccount',
    };

    const n = normalize(name);
    // exact or substring contains
    for (const [tr, key] of Object.entries(turkishToKeyMap)) {
      if (n === tr || n.includes(tr) || tr.includes(n)) {
        const translation = t(`catalog.names.${key}`);
        return translation === `catalog.names.${key}` ? name : translation;
      }
    }
    return name;
  };

  useEffect(() => {
    // Fetch catalogs on mount
    const fetchCatalogs = async () => {
      try {
        const lang = i18n.language?.startsWith('en') ? 'en' : 'tr';
        let { data } = await axios.get(`/api/catalogs?lang=${lang}`);
        // Fallback for legacy data: if Turkish view returns empty, fetch all and filter out English
        if (lang === 'tr' && (!Array.isArray(data) || data.length === 0)) {
          const all = await axios.get('/api/catalogs');
          data = Array.isArray(all.data) ? all.data.filter((c) => c.language !== 'en') : [];
        } else if (lang === 'tr' && Array.isArray(data)) {
          // Defensive filter: never show English in Turkish view
          data = data.filter((c) => c.language !== 'en');
        }

        // Process the data to ensure pdfUrl includes 'https://'
        const processedCatalogs = data.map((catalog) => {
          let { pdfUrl } = catalog;
          if (!pdfUrl.startsWith("http")) {
            pdfUrl = `https://${pdfUrl}`;
          }
          
          // Debug: Log the catalog names to see what we're working with
          console.log('Catalog name from backend:', catalog.name);
          
          return { ...catalog, pdfUrl };
        });

        setCatalogs(processedCatalogs);
      } catch (err) {
        setError(t('catalog.error_fetching'));
        console.error("Error fetching catalogs:", err);
      }
    };
    fetchCatalogs();
  }, [t, i18n.language]);

  const handleOpenPdf = (pdfUrl) => {
    window.open(pdfUrl, "_blank");
  };

  return (
    <div className="catalog-page">
      <h1>{t('catalog.title')}</h1>
      {error && <p className="error-message">{error}</p>}
      <div className="catalogs-grid">
        {catalogs.map(({ _id, name, pdfUrl }) => (
          <CatalogCard
            key={_id}
            name={name}
            translatedName={translateCatalogName(name)}
            pdfUrl={pdfUrl}
            onOpenPdf={handleOpenPdf}
          />
        ))}
      </div>
    </div>
  );
};

// Simple concurrency limiter so we don't render too many PDF thumbnails at once
let activeRenders = 0;
const MAX_CONCURRENT_RENDERS = 2;

const CatalogCard = ({ name, translatedName, pdfUrl, onOpenPdf }) => {
  const { t } = useTranslation();
  const [thumbnailUrl, setThumbnailUrl] = useState(null);
  const [visible, setVisible] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { rootMargin: "600px" }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!visible) return;
    const loadThumbnail = async () => {
      try {
        // Cache in sessionStorage to avoid re-rendering on revisit
        const cacheKey = `catalog-thumb:${pdfUrl}`;
        const cached = sessionStorage.getItem(cacheKey);
        if (cached) {
          setThumbnailUrl(cached);
          return;
        }
        // Wait for a render slot
        while (activeRenders >= MAX_CONCURRENT_RENDERS) {
          await new Promise((r) => setTimeout(r, 100));
        }
        activeRenders++;

        const loadingTask = pdfjsLib.getDocument({ url: pdfUrl, disableStream: false });
        const pdf = await loadingTask.promise;
        const page = await pdf.getPage(1);
        // Smaller scale for faster render
        const viewport = page.getViewport({ scale: 0.5 });
        const canvas = document.createElement("canvas");
        canvas.height = viewport.height;
        canvas.width = viewport.width;
        await page.render({ canvasContext: canvas.getContext("2d"), viewport })
          .promise;
        // Export as compressed JPEG at 55% quality
        const dataUrl = canvas.toDataURL("image/jpeg", 0.55);
        setThumbnailUrl(dataUrl);
        try { sessionStorage.setItem(cacheKey, dataUrl); } catch (_) {}
      } catch (err) {
        console.error("Thumbnail error:", err);
      } finally {
        activeRenders = Math.max(0, activeRenders - 1);
      }
    };
    loadThumbnail();
  }, [visible, pdfUrl]);

  return (
    <div className="catalog-card" ref={ref}>
      <div className="card-header">
        <h3 className="catalog-name" title={translatedName}>
          {translatedName}
        </h3>
      </div>
      <div className="pdf-preview" onClick={() => onOpenPdf(pdfUrl)}>
        {thumbnailUrl ? (
          // native lazy-loading:
          <img
            loading="lazy"
            src={thumbnailUrl}
            alt={`Preview of ${translatedName}`}
            decoding="async"
            style={{ objectFit: "contain" }}
          />
        ) : (
          <div className="loading-placeholder">{t('catalog.loading_preview')}</div>
        )}
      </div>
      <a
        href={pdfUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="download-pdf-btn"
      >
        <FaDownload /> {t('catalog.download')}
      </a>
    </div>
  );
};

export default Catalog;

